import {Alert, Button, message, Space, Spin, Tag} from "antd";
import {
  StyledScheduleAvailabilityFormWrapper,
  StyledUserScheduleItemChildWrapper,
  StyledUserScheduleItemHeadingWrapper,
  StyledUserScheduleItemTitle,
  StyledUserScheduleItemTitleDot,
  StyledUserScheduleItemTitleWrapper,
  StyledUserScheduleItemWrapper,
} from "./styles";
import {userScheduleAccordionReducer} from "../../reducers";
import {useUserScheduleAccordion} from "../../hooks/use-user-schedule-accordion";
import {AnimatePresence, motion} from "framer-motion";
import {DownOutlined} from "@ant-design/icons";
import {UserScheduleItemMenu} from "./menu";
import {useState} from "react";
import {ApplyScheduleToSessionFormModal} from "../apply-schedule-to-session-modal";
import {
  useAddDaysToScheduleApi,
  useGetUserSchedule,
} from "../../../../../../react-query-hooks/schedule";

import {yupResolver} from "@hookform/resolvers/yup";
import {useForm} from "react-hook-form";
import moment from "moment";
import {TAvailabilityApiResponse} from "../../../../../../react-query-hooks/availability";
import {IAvailabilityFormValues} from "../../../../../../components/calls/availability/i-availability-form";
import {DayTimePicker} from "../../../../../../components/day-time-picker";
import {StyledServiceButtonWrapper} from "../../../../../../styles/onboarding/onboarding";
import {AvailabilityFormSchema} from "../../../../../../components/calls/availability/availability-validation-schema";
import {AddDaysToScheduleApiPayload} from "../../../../../../react-query-hooks/schedule/types";
import {LoadingSpinner} from "../../../../../../components/icons/icons";
import {scrollToTop} from "../../../../../../utils/scroll-to-top";

const colorCycle = [
  {dotColor: "#2F54EB"}, // index % 4 === 0
  {dotColor: "#222222"}, // index % 4 === 1
  {dotColor: "#52C41A"}, // index % 4 === 2
  {dotColor: "#FAAD14"}, // index % 4 === 3
];

export type UserScheduleItemProps = {
  scheduleId: string;
  scheduleName: string;
  numberOfSessions: number;
  isDefault: boolean;
  index: number;
};

export function UserScheduleItem({
  scheduleId,
  scheduleName,
  numberOfSessions,
  isDefault,
  index,
}: UserScheduleItemProps) {
  const {dotColor} = colorCycle[index % 4];
  const [isApplyScheduleToSessionModalOpen, setIsApplyScheduleToSessionModalOpen] =
    useState(false);
  const {openIndexes, toggleIndex} = useUserScheduleAccordion({
    reducer: userScheduleAccordionReducer,
  });

  function handleCloseApplyScheduleToSessionModal() {
    setIsApplyScheduleToSessionModalOpen(false);
  }
  function handleApplyScheduleToSessionModalOk() {
    setIsApplyScheduleToSessionModalOpen(false);
  }

  function handleOpenApplyScheduleToSessionModal() {
    setIsApplyScheduleToSessionModalOpen(true);
  }
  // function generateRandomHexColor() {
  //   // Generate a random 24-bit integer (0 to 16777215)
  //   const randomInt = Math.floor(Math.random() * 0xffffff);

  //   // Convert the integer to a hexadecimal string
  //   let hexColor = randomInt.toString(16);

  //   // Pad with leading zeros if the hex string is less than 6 characters
  //   while (hexColor.length < 6) {
  //     hexColor = "0" + hexColor;
  //   }

  //   // Prepend '#' to form a valid hex color code
  //   return "#" + hexColor;
  // }

  // const dotHexColor = generateRandomHexColor();

  return (
    <div>
      <StyledUserScheduleItemWrapper>
        <StyledUserScheduleItemHeadingWrapper>
          <StyledUserScheduleItemTitleWrapper>
            <StyledUserScheduleItemTitleDot
            // style={{
            //   backgroundColor: "dotHexColor",
            // }}
            />
            <StyledUserScheduleItemTitle>{scheduleName}</StyledUserScheduleItemTitle>
            {isDefault && <Tag color="purple">Default</Tag>}
          </StyledUserScheduleItemTitleWrapper>
          <div>
            <Space size={10}>
              <UserScheduleItemMenu
                scheduleId={scheduleId}
                scheduleName={scheduleName}
                isDefault={isDefault}
                numberOfSessions={numberOfSessions}
                handleOpenApplyScheduleToSessionModal={
                  handleOpenApplyScheduleToSessionModal
                }
              />
              <Button
                icon={<DownOutlined rotate={openIndexes.includes(index) ? 0 : 180} />}
                shape="circle"
                type="text"
                size="small"
                onClick={() => toggleIndex(index)}
              />
            </Space>
          </div>
        </StyledUserScheduleItemHeadingWrapper>
        <div>
          <p>
            Applied to{" "}
            <span
              style={{
                textDecoration: "underline",
                fontWeight: 600,
                fontSize: "1.4rem",
                cursor: "pointer",
              }}
              onClick={handleOpenApplyScheduleToSessionModal}
            >
              {numberOfSessions} sessions
            </span>
          </p>
        </div>
      </StyledUserScheduleItemWrapper>
      <div>
        <AnimatePresence initial={false}>
          {openIndexes.includes(index) && (
            <ScheduleAvailability scheduleId={scheduleId} />
          )}
        </AnimatePresence>
      </div>
      <ApplyScheduleToSessionFormModal
        handleApplyScheduleToSessionModalOk={handleApplyScheduleToSessionModalOk}
        handleCloseApplyScheduleToSessionModal={handleCloseApplyScheduleToSessionModal}
        isApplyScheduleToSessionModalOpen={isApplyScheduleToSessionModalOpen}
        scheduleId={scheduleId}
        scheduleName={scheduleName}
      />
    </div>
  );
}

export function ScheduleAvailability({scheduleId}: {scheduleId: string}) {
  const getUserSchedule = useGetUserSchedule(scheduleId);

  if (getUserSchedule.isLoading) {
    return (
      <div style={{display: "flex", justifyContent: "center", width: "100%"}}>
        <Spin indicator={LoadingSpinner} />
      </div>
    );
  }
  if (getUserSchedule.isError) {
    return <Alert message={getUserSchedule.error.message} type="error" />;
  }

  function handleAvailabilities(): TAvailabilityApiResponse {
    if (getUserSchedule.data) {
      return {
        fri: getUserSchedule.data.availabilities.fri.map((s) => ({
          from: s.from,
          to: s.to,
        })),
        mon: getUserSchedule.data.availabilities.mon.map((s) => ({
          from: s.from,
          to: s.to,
        })),
        sat: getUserSchedule.data.availabilities.sat.map((s) => ({
          from: s.from,
          to: s.to,
        })),
        sun: getUserSchedule.data.availabilities.sun.map((s) => ({
          from: s.from,
          to: s.to,
        })),
        thu: getUserSchedule.data.availabilities.thu.map((s) => ({
          from: s.from,
          to: s.to,
        })),
        tue: getUserSchedule.data.availabilities.tue.map((s) => ({
          from: s.from,
          to: s.to,
        })),
        wed: getUserSchedule.data.availabilities.wed.map((s) => ({
          from: s.from,
          to: s.to,
        })),
      };
    } else {
      return {
        fri: [],
        mon: [],
        sat: [],
        sun: [],
        thu: [],
        tue: [],
        wed: [],
      };
    }
  }

  const availabilities = handleAvailabilities();
  return (
    <StyledUserScheduleItemChildWrapper>
      <motion.div
        key="content"
        initial="collapsed"
        animate="open"
        exit="collapsed"
        variants={{
          open: {opacity: 1, height: "auto"},
          collapsed: {opacity: 0, height: 0},
        }}
        transition={{duration: 0.3, ease: "linear"}}
      >
        <ScheduleAvailabilityForm
          availabilities={availabilities}
          scheduleId={scheduleId}
        />
      </motion.div>
    </StyledUserScheduleItemChildWrapper>
  );
}

export const ScheduleAvailabilityForm = ({
  availabilities,
  scheduleId,
}: {
  availabilities: TAvailabilityApiResponse;
  scheduleId: string;
}) => {
  const addDaysToScheduleMutation = useAddDaysToScheduleApi();

  const {
    handleSubmit,
    formState: {errors},
    watch,
    setValue,
    reset,
    control,
  } = useForm<IAvailabilityFormValues>({
    resolver: yupResolver(AvailabilityFormSchema),
    mode: "onChange",
    defaultValues: {
      sun: {
        isAvailable: availabilities.sun.length > 0,
        timesAvailable:
          availabilities.sun.length > 0
            ? availabilities.sun
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
      mon: {
        isAvailable: availabilities.mon.length > 0,
        timesAvailable:
          availabilities.mon.length > 0
            ? availabilities.mon
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
      tue: {
        isAvailable: availabilities.tue.length > 0,
        // isAvailable: true,
        timesAvailable:
          availabilities.tue.length > 0
            ? availabilities.tue
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
      wed: {
        isAvailable: availabilities.wed.length > 0,
        // isAvailable: true,
        timesAvailable:
          availabilities.wed.length > 0
            ? availabilities.wed
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
      thu: {
        isAvailable: availabilities.thu.length > 0,
        timesAvailable:
          availabilities.thu.length > 0
            ? availabilities.thu
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
      fri: {
        isAvailable: availabilities.fri.length > 0,
        // isAvailable: true,
        timesAvailable:
          availabilities.fri.length > 0
            ? availabilities.fri
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
      sat: {
        isAvailable: availabilities.sat.length > 0,
        // isAvailable: true,
        timesAvailable:
          availabilities.sat.length > 0
            ? availabilities.sat
            : [{from: "09:00 AM", to: "05:00 PM"}],
      },
    },
  });

  const sortTimeSlot = (
    arr: {from: string; to: string}[]
  ): {from: string; to: string}[] => {
    const mapResult = arr.map((entry) => {
      const fromEntry = moment(`11-18-2013 ${entry.from}`, "MM-DD-YYYY hh:mm A", true);
      return {
        from: fromEntry,
        to: entry.to,
      };
    });
    const sortResult = mapResult.sort((a, b) => a.from.diff(b.from));
    const finalResult = sortResult.map((res) => ({
      from: res.from.format("hh:mm A"),
      to: res.to,
    }));
    // console.log({finalResult});
    return finalResult;
  };

  const onSubmit = async (values: IAvailabilityFormValues) => {
    const timeEntries = Object.entries(values);
    const payload: AddDaysToScheduleApiPayload = {
      scheduleId,
      fri: [{from: "09:00 AM", to: "05:00 PM"}],
      mon: [{from: "09:00 AM", to: "05:00 PM"}],
      sat: [{from: "09:00 AM", to: "05:00 PM"}],
      sun: [{from: "09:00 AM", to: "05:00 PM"}],
      thu: [{from: "09:00 AM", to: "05:00 PM"}],
      tue: [{from: "09:00 AM", to: "05:00 PM"}],
      wed: [{from: "09:00 AM", to: "05:00 PM"}],
    };
    // console.log({timeEntries});
    timeEntries.forEach((entry) => {
      if (entry[0] === "mon") {
        if (entry[1].isAvailable) {
          payload.mon = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.mon = [];
        }
      }
      if (entry[0] === "tue") {
        if (entry[1].isAvailable) {
          payload.tue = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.tue = [];
        }
      }
      if (entry[0] === "wed") {
        if (entry[1].isAvailable) {
          payload.wed = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.wed = [];
        }
      }
      if (entry[0] === "thu") {
        if (entry[1].isAvailable) {
          payload.thu = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.thu = [];
        }
      }
      if (entry[0] === "fri") {
        if (entry[1].isAvailable) {
          payload.fri = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.fri = [];
        }
      }
      if (entry[0] === "sat") {
        if (entry[1].isAvailable) {
          payload.sat = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.sat = [];
        }
      }
      if (entry[0] === "sun") {
        if (entry[1].isAvailable) {
          payload.sun = sortTimeSlot(
            entry[1].timesAvailable.map((time: any) => ({
              from: time.from,
              to: time.to,
            }))
          );
        } else {
          payload.sun = [];
        }
      }
    });
    // dispatch(createAvailability(payload));
    // console.log(moment("Mon Oct 03 2022 17:00:00 GMT+0100", "hh:mm a").format("hh:mm a"));
    // console.log({payload});
    addDaysToScheduleMutation.mutate(payload, {
      onSuccess: () => {
        message.success("Your availability preferences has been updated successfully");
        // scrollToTop(".ant-drawer-body");
      },
    });
  };

  return (
    <div>
      <Space size={24} direction="vertical" style={{display: "Flex"}}>
        <StyledScheduleAvailabilityFormWrapper>
          <p className="availability-form-heading">
            Set your availability and weekly work hours
          </p>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div style={{marginBottom: "2rem"}}>
              <DayTimePicker
                watch={watch}
                setValue={setValue}
                checkboxLabel="SUN"
                checkboxId="SUN"
                checkboxName="sun.isAvailable"
                // checkboxError={errors.sunday?.isAvailable?.message}
                control={control}
                timePickerId="sun"
                timePickerName="sun.timesAvailable"
                timePickerError={errors.sun}
              />

              <DayTimePicker
                watch={watch}
                setValue={setValue}
                checkboxLabel="MON"
                checkboxId="mon"
                checkboxName="mon.isAvailable"
                // checkboxError={errors.monday?.isAvailable?.message}
                control={control}
                timePickerId="mon"
                timePickerName="mon.timesAvailable"
                timePickerError={errors.mon}
              />
              <DayTimePicker
                watch={watch}
                setValue={setValue}
                checkboxLabel="TUE"
                checkboxId="tue"
                checkboxName="tue.isAvailable"
                // checkboxError={errors.monday?.isAvailable?.message}
                control={control}
                timePickerId="tue"
                timePickerName="tue.timesAvailable"
                timePickerError={errors.tue}
              />
              <DayTimePicker
                watch={watch}
                setValue={setValue}
                checkboxLabel="WED"
                checkboxId="wed"
                checkboxName="wed.isAvailable"
                // checkboxError={errors.monday?.isAvailable?.message}
                control={control}
                timePickerId="wed"
                timePickerName="wed.timesAvailable"
                timePickerError={errors.wed}
              />
              <DayTimePicker
                watch={watch}
                setValue={setValue}
                checkboxLabel="THU"
                checkboxId="thu"
                checkboxName="thu.isAvailable"
                // checkboxError={errors.monday?.isAvailable?.message}
                control={control}
                timePickerId="thu"
                timePickerName="thu.timesAvailable"
                timePickerError={errors.thu}
              />
              <DayTimePicker
                watch={watch}
                setValue={setValue}
                checkboxLabel="FRI"
                checkboxId="fri"
                checkboxName="fri.isAvailable"
                // checkboxError={errors.monday?.isAvailable?.message}
                control={control}
                timePickerId="fri"
                timePickerName="fri.timesAvailable"
                timePickerError={errors.fri}
              />
              <DayTimePicker
                watch={watch}
                setValue={setValue}
                checkboxLabel="SAT"
                checkboxId="SAT"
                checkboxName="sat.isAvailable"
                // checkboxError={errors.monday?.isAvailable?.message}
                control={control}
                timePickerId="sat"
                timePickerName="sat.timesAvailable"
                timePickerError={errors.sat}
              />

              <StyledServiceButtonWrapper style={{marginTop: "2.4rem"}}>
                <div>
                  <Button
                    loading={addDaysToScheduleMutation.isLoading}
                    block
                    type="primary"
                    htmlType="submit"
                    size="small"
                  >
                    Save changes
                  </Button>
                </div>
                <div>
                  <Button onClick={() => reset()} block size="small">
                    Discard changes
                  </Button>
                </div>
              </StyledServiceButtonWrapper>
            </div>
          </form>
        </StyledScheduleAvailabilityFormWrapper>
      </Space>
    </div>
  );
};
